import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactElement } from 'react';
import { Pencil, StickyNote } from 'lucide-react';
import { useRepertoireEntry, useUpdateRepertoireEntry } from '@/lib/queries/repertoire';
import { useKeys, useMasteryLevels, useQueueStatuses, useTimeSignatures } from '@/lib/queries/reference-data';
import { useArrangementComments, useAddComment, useDeleteComment, usePracticeHistory } from '@/lib/queries/pieces';
import { useArrangementResources, useAddResourceLink, useUploadResourceFile, useDeleteResource } from '@/lib/queries/resources';
import { MasteryBadge } from '@/components/molecules/mastery-badge';
import { StatTile } from '@/components/molecules/stat-tile';
import { BottomSheet } from '@/components/molecules/bottom-sheet';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { Icon } from '@/components/atoms/icon';
import { Micro } from '@/components/atoms/micro';
import { ResourceSection } from '@/components/resource-section';
import { CommentSection } from '@/components/comment-section';
import { RepertoireEntryForm, type RepertoireEntryFormData } from '@/components/organisms/repertoire-entry-form';
import { RecordTakeFlow } from '@/components/organisms/record-take-flow';
import { TakesStrip } from '@/components/organisms/takes-strip';
import { TakesSection } from '@/components/organisms/takes-section';
import type { PracticeHistoryEntry, RepertoireEntry } from '@/lib/types';

export const Route = createFileRoute('/repertoire_/$arrangementId')({
  component: RepertoireDetailPage,
});

function formatDateLong(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  return Math.max(0, Math.floor((now - then) / (1000 * 60 * 60 * 24)));
}

function EntryHeader({
  entry,
  masterySortOrder,
  onEditMobile,
}: {
  entry: RepertoireEntry;
  masterySortOrder: number | null;
  onEditMobile: () => void;
}): ReactElement {
  const subtitle = [entry.arrangementName, entry.keyName, entry.formName].filter(Boolean).join(' • ');
  return (
    <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 md:px-8 pt-6 pb-4">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="flex items-center justify-between gap-2">
          <Link to="/repertoire">
            <Button variant="ghost" size="sm">
              <Icon name="chev_l" size={16} />
              Repertoire
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <RecordTakeFlow arrangementId={entry.arrangementId} />
            <Link
              to="/repertoire/$arrangementId/edit"
              params={{ arrangementId: entry.arrangementId }}
              className="hidden lg:inline-flex"
            >
              {/* TODO: edit icon — see BACKLOG.md */}
              <Button size="sm">
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            </Link>
            <Button size="sm" onClick={onEditMobile} className="lg:hidden">
              {/* TODO: edit icon — see BACKLOG.md */}
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h1 className="font-display text-[28px] md:text-[44px] font-semibold tracking-tight leading-[1.05] text-[var(--ink)]">
              {entry.pieceName}
            </h1>
            <p className="mt-2 text-[13px] text-[var(--ink-3)]">
              {subtitle || 'Arrangement'}
            </p>
            {entry.pieceId && (
              <Link
                to="/library/$pieceId"
                params={{ pieceId: entry.pieceId }}
                className="mt-1 inline-block text-[12px] font-semibold text-[var(--brand)] hover:underline"
              >
                View piece →
              </Link>
            )}
          </div>
          <div className="shrink-0">
            <MasteryBadge sortOrder={masterySortOrder ?? undefined} />
          </div>
        </div>
      </div>
    </header>
  );
}

function ArrangementInfo({ entry }: { entry: RepertoireEntry }): ReactElement {
  const rows: { label: string; value: string }[] = [
    { label: 'Arrangement', value: entry.arrangementName || '—' },
    { label: 'Key Signature', value: entry.keyName || '—' },
    { label: 'Form', value: entry.formName || '—' },
    { label: 'Queue Status', value: entry.queueStatusName || '—' },
    {
      label: 'Practice Tempo',
      value: entry.practiceTempoBeats ? `${entry.practiceTempoBeats} bpm` : '—',
    },
    {
      label: 'Performance Tempo',
      value: entry.performanceTempoBeats ? `${entry.performanceTempoBeats} bpm` : '—',
    },
  ];
  return (
    <Card padding="lg">
      <Micro className="mb-4">Arrangement Info</Micro>
      <div className="mt-4 grid grid-cols-2 gap-y-4 gap-x-2 md:grid-cols-3">
        {rows.map((row) => (
          <div key={row.label}>
            <Micro>{row.label}</Micro>
            <p className="mt-1 text-sm font-medium text-[var(--ink)]">{row.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PracticeHistoryExcerpt({ history }: { history: PracticeHistoryEntry[] }): ReactElement {
  const recent = history.slice(0, 5);
  return (
    <Card padding="lg">
      <div className="flex items-center justify-between mb-4">
        <Micro>Recent Practice</Micro>
        <Icon name="clock" size={16} className="text-[var(--ink-3)]" />
      </div>
      {recent.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--ink-3)]">No practice sessions yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--rule)]">
          {recent.map((session) => (
            <li key={session.practiceSessionId} className="flex items-center justify-between py-2 text-sm">
              <div className="flex items-center gap-2 text-[var(--ink)]">
                <Icon name="play" size={16} className="text-[var(--brand)]" />
                <span>{formatDateShort(session.practicedAt)}</span>
                {session.instrumentName && (
                  <span className="text-xs text-[var(--ink-3)]">· {session.instrumentName}</span>
                )}
              </div>
              {session.durationMinutes != null && (
                <span className="text-xs font-medium text-[var(--ink-3)]">
                  {session.durationMinutes}m
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RepertoireDetailPage(): ReactElement {
  const { arrangementId } = Route.useParams();
  const { data: entry, isLoading, isError } = useRepertoireEntry(arrangementId);
  const { data: masteryData } = useMasteryLevels();
  const { data: queueData } = useQueueStatuses();
  const { data: keyData } = useKeys();
  const { data: timeSignatureData } = useTimeSignatures();
  const { data: practiceHistory } = usePracticeHistory(arrangementId);
  const { data: comments } = useArrangementComments(arrangementId);
  const { data: resources } = useArrangementResources(arrangementId);

  const addComment = useAddComment();
  const deleteComment = useDeleteComment();
  const addResourceLink = useAddResourceLink();
  const uploadResourceFile = useUploadResourceFile();
  const deleteResource = useDeleteResource();
  const updateEntry = useUpdateRepertoireEntry();

  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  const masteryLevels = masteryData?.content ?? [];
  const masterySortOrder = useMemo(() => {
    if (!entry?.masteryLevelId) return null;
    const found = masteryLevels.find((level) => level.masteryLevelId === entry.masteryLevelId);
    return found?.sortOrder ?? null;
  }, [entry?.masteryLevelId, masteryLevels]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-page)] text-sm text-[var(--ink-3)]">
        Loading…
      </div>
    );
  }

  if (isError || !entry) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-page)] p-6 text-center">
        <p className="font-display text-xl font-semibold text-[var(--ink)]">Arrangement not found</p>
        <p className="text-sm text-[var(--ink-3)]">It may have been removed from your repertoire.</p>
        <Link to="/repertoire">
          <Button variant="secondary" size="sm">
            <Icon name="chev_l" size={16} /> Back to repertoire
          </Button>
        </Link>
      </div>
    );
  }

  const totalSessions = practiceHistory?.length ?? 0;
  const lastSession = practiceHistory?.[0];
  const daysSinceLast = daysSince(lastSession?.practicedAt ?? null);
  const latestAssessment = lastSession?.assessmentLevelName;

  const handleEditSave = async (data: RepertoireEntryFormData): Promise<void> => {
    await updateEntry.mutateAsync({
      arrangementId,
      masteryLevelId: data.masteryLevelId ?? undefined,
      queueStatusId: data.queueStatusId,
      keyId: data.keyId ?? undefined,
      practiceTempoBeats: data.practiceTempoBeats ?? undefined,
      performanceTempoBeats: data.performanceTempoBeats ?? undefined,
      notes: data.notes,
    });
    setIsEditSheetOpen(false);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pb-24 text-[var(--ink)]">
      <EntryHeader entry={entry} masterySortOrder={masterySortOrder} onEditMobile={() => setIsEditSheetOpen(true)} />

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-8 md:px-8">
        <section>
          <Micro className="mb-3">At a Glance</Micro>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile
              label="Total Sessions"
              value={String(totalSessions)}
              icon="music"
              tone="neutral"
            />
            <StatTile
              label="Last Practiced"
              value={lastSession ? formatDateLong(lastSession.practicedAt) : 'Never'}
              tone="brand"
              delta={daysSinceLast != null ? `${daysSinceLast} day${daysSinceLast === 1 ? '' : 's'} ago` : undefined}
            />
            <StatTile
              label="Latest Assessment"
              value={latestAssessment || '—'}
              icon="flame"
              tone="warm"
            />
            <StatTile
              label="Mastery"
              value={entry.masteryLevelName || 'Not set'}
              icon="flame"
              tone="warm"
            />
          </div>
        </section>

        <ArrangementInfo entry={entry} />

        <PracticeHistoryExcerpt history={practiceHistory ?? []} />

        <TakesStrip arrangementId={arrangementId} />

        <TakesSection arrangementId={arrangementId} />

        <Card padding="lg">
          <Micro className="mb-4">Resources</Micro>
          <ResourceSection
            resources={resources ?? []}
            onAddLink={(data) => addResourceLink.mutate({ arrangementId, ...data })}
            onUploadFile={(file, resourceType, title) =>
              uploadResourceFile.mutate({ arrangementId, file, resourceType, title })
            }
            onDelete={(resourceId) => deleteResource.mutate({ arrangementId, resourceId })}
            uploadStatus={{
              isPending: uploadResourceFile.isPending,
              isError: uploadResourceFile.isError,
              isSuccess: uploadResourceFile.isSuccess,
              errorMessage: uploadResourceFile.error?.message,
            }}
          />
        </Card>

        <Card padding="lg" data-testid="notes-section">
          <div className="mb-4 flex items-center justify-between">
            <Micro>Notes</Micro>
            {/* TODO: notes/sticky-note icon — see BACKLOG.md */}
            <StickyNote className="h-4 w-4 text-[var(--ink-3)]" />
          </div>
          <CommentSection
            comments={comments ?? []}
            onAdd={(content) => addComment.mutate({ arrangementId, content })}
            onDelete={(commentId) => deleteComment.mutate({ arrangementId, commentId })}
            error={addComment.error?.message ?? deleteComment.error?.message ?? null}
          />
        </Card>
      </main>

      <BottomSheet open={isEditSheetOpen} onClose={() => setIsEditSheetOpen(false)} title="Edit arrangement">
        <RepertoireEntryForm
          mode="edit"
          entry={entry}
          masteryLevels={masteryData?.content ?? []}
          queueStatuses={queueData?.content ?? []}
          keys={keyData?.content ?? []}
          timeSignatures={timeSignatureData?.content ?? []}
          onSave={(data) => {
            void handleEditSave(data);
          }}
          onCancel={() => setIsEditSheetOpen(false)}
          isSaving={updateEntry.isPending}
          showDangerZone={false}
          showSharedMetadata={false}
        />
      </BottomSheet>
    </div>
  );
}
